import { AlertTriangle, Check } from "lucide-react"

interface CheckboxCardProps {
  checked: boolean
  onChange: (checked: boolean) => void
  label: string
  description?: string
  warningWhenChecked?: boolean
  disabled?: boolean
}

/**
 * Styled checkbox card for environmental observations.
 * Shows warning styling when checked (for negative observations like algal bloom).
 */
export function CheckboxCard({
  checked,
  onChange,
  label,
  description,
  warningWhenChecked = false,
  disabled = false,
}: CheckboxCardProps) {
  const isWarning = warningWhenChecked && checked

  return (
    <button
      type="button"
      onClick={() => !disabled && onChange(!checked)}
      disabled={disabled}
      className={`
        w-full flex items-start gap-3 p-4 rounded-lg border-2 text-left transition-all
        ${disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer hover:shadow-sm"}
        ${
          checked
            ? isWarning
              ? "border-orange-400 bg-orange-50"
              : "border-emerald-400 bg-emerald-50"
            : "border-slate-200/50 bg-white hover:border-slate-300"
        }
      `}
    >
      {/* Checkbox indicator */}
      <div
        className={`
          flex-shrink-0 w-5 h-5 rounded border-2 flex items-center justify-center mt-0.5
          ${
            checked
              ? isWarning
                ? "border-orange-500 bg-orange-500"
                : "border-emerald-500 bg-emerald-500"
              : "border-slate-300 bg-white"
          }
        `}
      >
        {checked && (
          isWarning ? (
            <AlertTriangle className="w-3 h-3 text-white" />
          ) : (
            <Check className="w-3 h-3 text-white" />
          )
        )}
      </div>

      {/* Label and description */}
      <div className="flex-1 min-w-0">
        <div
          className={`text-sm font-medium ${
            checked
              ? isWarning
                ? "text-orange-800"
                : "text-emerald-800"
              : "text-gray-900"
          }`}
        >
          {label}
        </div>
        {description && (
          <div
            className={`text-xs mt-0.5 ${
              checked
                ? isWarning
                  ? "text-orange-600"
                  : "text-emerald-600"
                : "text-gray-500"
            }`}
          >
            {description}
          </div>
        )}
      </div>
    </button>
  )
}
